import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Device, Call } from '@twilio/voice-sdk';
import { supabase } from '../lib/supabase';
import { Phone, PhoneOff, Wallet, X } from 'lucide-react';
import { useAuth } from './AuthContext';

type CallStatus = 'idle' | 'connecting' | 'ringing' | 'connected' | 'disconnected' | 'error';

interface VoiceCallContextType {
  startCall: (advisorId: string, advisorName: string, perMinuteRate: number) => Promise<void>;
  endCall: () => void;
  isCallActive: boolean;
}

const VoiceCallContext = createContext<VoiceCallContextType | undefined>(undefined);

export const useVoiceCall = () => {
  const context = useContext(VoiceCallContext);
  if (!context) {
    throw new Error('useVoiceCall must be used within VoiceCallProvider');
  }
  return context;
};

const formatDuration = (s: number) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

export const VoiceCallProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [call, setCall] = useState<Call | null>(null);
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [freeMinutes, setFreeMinutes] = useState(0);
  const [duration, setDuration] = useState(0);
  const [advisorName, setAdvisorName] = useState('');
  const [perMinuteRate, setPerMinuteRate] = useState(0);
  const deviceRef = useRef<Device | null>(null);

  const isCallActive = callStatus === 'connecting' || callStatus === 'ringing' || callStatus === 'connected';

  const fetchWalletAndCredits = async () => {
    if (!user) return;
    try {
      const { data: walletData } = await supabase.from('user_wallets').select('balance').eq('user_id', user.id).single();
      setWalletBalance(walletData?.balance || 0);
      const { data: creditsData } = await supabase.from('user_credits').select('free_minutes_remaining').eq('user_id', user.id).maybeSingle();
      setFreeMinutes(creditsData?.free_minutes_remaining || 0);
    } catch (err) {
      console.error('Error fetching wallet/credits info:', err);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    let poller: NodeJS.Timeout | undefined;

    if (callStatus === 'connected') {
      timer = setInterval(() => setDuration(prev => prev + 1), 1000);
      poller = setInterval(fetchWalletAndCredits, 20000);
    } else {
      setDuration(0);
    }
    return () => {
      clearInterval(timer);
      clearInterval(poller);
    };
  }, [callStatus]);

  const cleanupConnection = () => {
    if (deviceRef.current) {
      deviceRef.current.destroy();
      deviceRef.current = null;
    }
    setCall(null);
    setCallStatus('idle');
  };

  const endCall = () => {
    if (call) {
      call.disconnect();
    }
    cleanupConnection();
    setIsModalOpen(false);
    setError(null);
  };

  const startCall = async (advisorId: string, advisorNameParam: string, perMinuteRateParam: number) => {
    if (!user) {
      alert("You must be logged in.");
      return;
    }

    setError(null);
    setAdvisorName(advisorNameParam);
    setPerMinuteRate(perMinuteRateParam);

    try {
      await fetchWalletAndCredits();

      const { data: walletData } = await supabase.from('user_wallets').select('balance').eq('user_id', user.id).single();
      const { data: creditsData } = await supabase.from('user_credits').select('free_minutes_remaining').eq('user_id', user.id).maybeSingle();
      if ((creditsData?.free_minutes_remaining || 0) <= 0 && (walletData?.balance || 0) < perMinuteRateParam) {
        throw new Error("Insufficient funds for this call.");
      }

      setIsModalOpen(true);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Authentication session expired.');

      const tokenResponse = await fetch(`${supabaseUrl}/functions/v1/generateVoiceToken`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ advisorId })
      });
      if (!tokenResponse.ok) throw new Error(`Token Error: ${await tokenResponse.text()}`);
      const { token } = await tokenResponse.json();
      if (!token) throw new Error('Failed to retrieve token.');

      const device = new Device(token, { logLevel: 1 });
      deviceRef.current = device;
      await device.register();

      setCallStatus('connecting');
      const outgoingCall = await device.connect({ params: { advisorId } });
      setCall(outgoingCall);

      outgoingCall.on('accept', () => setCallStatus('connected'));
      outgoingCall.on('disconnect', () => {
        cleanupConnection();
        setIsModalOpen(false);
      });
      outgoingCall.on('cancel', () => {
        cleanupConnection();
        setIsModalOpen(false);
      });
      outgoingCall.on('reject', () => {
        cleanupConnection();
        setIsModalOpen(false);
      });
      outgoingCall.on('error', (err) => {
        setError(err.message);
        setCallStatus('error');
      });
      outgoingCall.on('ringing', () => setCallStatus('ringing'));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      setCallStatus('error');
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (user) {
      fetchWalletAndCredits();
    }
  }, [user]);

  const modal = isModalOpen ? (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isCallActive) {
          endCall();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm relative text-center">
        {!isCallActive && (
          <button onClick={endCall} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        )}
        <div className="flex items-center justify-center mb-2">
          <Phone className="w-8 h-8 text-blue-600 mr-2" />
          <h3 className="text-xl font-semibold text-gray-800">Voice Call</h3>
        </div>
        <p className="text-gray-600 mb-4">with {advisorName}</p>
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-center mb-2">
            <Wallet className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-sm font-medium text-gray-700">Your Balance</span>
          </div>
          <div className="text-lg font-bold text-green-600">${walletBalance.toFixed(2)}</div>
          {freeMinutes > 0 && <div className="text-sm text-blue-600 mt-1">+ {freeMinutes} free minutes</div>}
          <div className="text-xs text-gray-500 mt-1">Rate: ${perMinuteRate.toFixed(2)}/minute</div>
        </div>
        <div className="mb-4 h-12 flex flex-col justify-center items-center">
          <p className="text-lg font-semibold text-gray-800">
            {callStatus.charAt(0).toUpperCase() + callStatus.slice(1)}
          </p>
          {callStatus === 'connected' && (
            <p className="text-2xl font-mono text-gray-900 mt-1">{formatDuration(duration)}</p>
          )}
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}
        <button
          onClick={endCall}
          className="w-full py-3 rounded-lg font-semibold bg-red-600 hover:bg-red-700 text-white flex items-center justify-center"
        >
          <PhoneOff className="w-5 h-5 mr-2" />
          End Call
        </button>
      </div>
    </div>
  ) : null;

  return (
    <VoiceCallContext.Provider value={{ startCall, endCall, isCallActive }}>
      {children}
      {modal && createPortal(modal, document.body)}
    </VoiceCallContext.Provider>
  );
};
